// bluaIQ — Pro Feature Gate
// Shows an upgrade prompt when a STARTER user tries to access a PRO/BUILDER feature.
// Wraps content and shows a lock overlay if the feature requires a higher plan.
// Enhanced: shows list of features they'd unlock + blurred preview

import { Crown, Lock, Check, Sparkles, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useSubscriptionPlan } from '../hooks/useSubscriptionPlan'
import { getProGateOrder, getProGatePrices } from '../shared/plans'

interface Props {
  requiredPlan: 'PRO' | 'BUILDER'
  currentPlan: string
  featureName: string
  featureNameEs?: string
  description?: string
  descriptionEs?: string
  isEs?: boolean
  children: React.ReactNode
  /** inline = small badge, block = full overlay */
  mode?: 'inline' | 'block'
  /** Features the user would unlock by upgrading */
  unlockFeatures?: string[]
  unlockFeaturesEs?: string[]
}

// Plan rank + upgrade prices derive from the shared plan catalog
// (src/shared/plans.ts) — identical values (STARTER 0/PRO 1/BUILDER 2; $89/$209).
const PLAN_ORDER = getProGateOrder()

const PLAN_PRICES = getProGatePrices()

// Default feature lists per plan
const PRO_FEATURES_EN = [
  'Finish Selections Wizard',
  'Automatic Expense Sheet',
  'Bid Comparison (scope gaps)',
  'Auto Payment Calendar from contracts',
  'Missing Payment Terms Alerts',
  'Full Gantt with dependencies',
  'AI Email Generator',
  'Unlimited AI Scans',
  'Unlimited Documents & Photos',
]

const PRO_FEATURES_ES = [
  'Wizard de Selección de Acabados',
  'Hoja de Gastos Automática',
  'Comparación de Propuestas (vacíos de alcance)',
  'Calendario de Pagos desde contratos',
  'Alertas de contratos sin términos de pago',
  'Gantt completo con dependencias',
  'Generador de Emails con IA',
  'Scans de IA ilimitados',
  'Documentos y Fotos ilimitados',
]

const BUILDER_FEATURES_EN = [
  'Everything in PRO, plus:',
  'WhatsApp Business API (direct send)',
  'SMS direct from app',
  'Message Templates library',
  'Auto Design RFQ generation',
  '30 active projects',
  '10 team members',
  '50 GB storage',
]

const BUILDER_FEATURES_ES = [
  'Todo lo del PRO, más:',
  'WhatsApp Business API (envío directo)',
  'SMS directo desde la app',
  'Biblioteca de Templates de mensajes',
  'Generación automática de RFQ de diseño',
  '30 proyectos activos',
  '10 miembros del equipo',
  '50 GB almacenamiento',
]

export default function ProFeatureGate({
  requiredPlan, currentPlan, featureName, featureNameEs, description, descriptionEs,
  isEs, children, mode = 'block', unlockFeatures, unlockFeaturesEs
}: Props) {
  const navigate = useNavigate()
  // PR-NEW: read `isLoading` directly from the shared
  // subscription hook so we can render a skeleton while the
  // plan query is still in flight. Without this, the hook's
  // fail-closed STARTER default made every PRO/BUILDER route
  // briefly render the upgrade prompt for ALL users (including
  // actual BUILDER subscribers) until the query resolved —
  // which the user saw as a flash of "Membership" before
  // their actual content loaded.
  const { isLoading: planLoading, plan: storedPlan, accessMode } = useSubscriptionPlan()
  // Codex PR2 R2 [P1]: en solo lectura, TODOS los gates (rutas y gates internos
  // como la hoja de gastos de Recibos o las alertas de Pagos) dejan VER lo del
  // plan que se pagó. Escribir lo bloquea el servidor (402) + banner.
  const passedLevel = PLAN_ORDER[currentPlan as keyof typeof PLAN_ORDER] ?? 0
  const readOnlyLevel = accessMode === 'read_only' && storedPlan
    ? (PLAN_ORDER[storedPlan as keyof typeof PLAN_ORDER] ?? 0)
    : 0
  const currentLevel = Math.max(passedLevel, readOnlyLevel)
  const requiredLevel = PLAN_ORDER[requiredPlan]

  // Plan still loading — render a neutral skeleton so we don't
  // flash an upgrade upsell for users who actually have access.
  // requirePlan on the server stays authoritative; this only
  // affects what the FE shows during the in-flight window.
  if (planLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={20} className="text-[#9E9890] animate-spin" />
      </div>
    )
  }

  // User has access
  if (currentLevel >= requiredLevel) {
    return <>{children}</>
  }

  const label = isEs ? featureNameEs || featureName : featureName
  const desc = isEs ? descriptionEs || description : description
  const price = PLAN_PRICES[requiredPlan]

  // Get feature list
  const defaultFeatures = requiredPlan === 'BUILDER'
    ? (isEs ? BUILDER_FEATURES_ES : BUILDER_FEATURES_EN)
    : (isEs ? PRO_FEATURES_ES : PRO_FEATURES_EN)
  const features = (isEs ? unlockFeaturesEs : unlockFeatures) || defaultFeatures

  if (mode === 'inline') {
    return (
      <div className="relative group">
        <div className="opacity-40 pointer-events-none select-none">{children}</div>
        <button
          onClick={() => navigate('/subscription')}
          className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[1px] rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <div className="flex items-center gap-1.5 bg-[#06539B] text-white text-[10px] font-[800] px-3 py-1.5 rounded-full shadow-lg">
            <Crown size={11} />
            {requiredPlan}
          </div>
        </button>
      </div>
    )
  }

  // Block mode — full overlay with blurred preview + feature list
  return (
    <div className="relative min-h-[500px]">
      {/* Blurred preview of the actual content */}
      <div className="opacity-20 pointer-events-none select-none blur-[2px] scale-[0.98]">{children}</div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-start justify-center pt-12 bg-gradient-to-b from-white/80 via-white/95 to-white">
        <div className="bg-white border border-[#ECEAE4] rounded-2xl p-8 shadow-2xl shadow-black/5 max-w-[420px] w-full mx-4">
          {/* Lock icon */}
          <div className="w-[56px] h-[56px] rounded-2xl bg-gradient-to-br from-[#06539B] to-[#00BCD4] flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#06539B]/20">
            <Lock size={24} className="text-white" />
          </div>

          {/* Feature name */}
          <h3 className="text-[18px] font-[900] text-[#1C1A17] text-center tracking-[-0.5px]">
            {label}
          </h3>

          {/* Description */}
          {desc && (
            <p className="text-[12px] font-[500] text-[#9E9890] mt-2 text-center leading-[1.6]">
              {desc}
            </p>
          )}

          {/* Plan badge */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <span className="text-[10px] font-[800] px-3 py-1 rounded-full bg-[#06539B]/10 text-[#06539B] uppercase tracking-[0.5px]">
              {isEs ? `Plan ${requiredPlan}` : `${requiredPlan} Plan`}
            </span>
            <span className="text-[12px] font-[900] text-[#1C1A17]">
              {price}/{isEs ? 'mes' : 'mo'}
            </span>
          </div>

          {/* Feature list */}
          <div className="mt-5 bg-[#FAFAF8] rounded-xl p-4 border border-[#F2F0EB]">
            <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-2.5">
              {isEs ? 'Lo que desbloqueas' : 'What you unlock'}
            </div>
            <div className="space-y-1.5">
              {features.slice(0, 7).map((f, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Check size={12} className="text-[#22C55E] mt-0.5 flex-shrink-0" />
                  <span className="text-[11px] font-[600] text-[#1C1A17] leading-[1.4]">{f}</span>
                </div>
              ))}
              {features.length > 7 && (
                <div className="text-[10px] font-[700] text-[#06539B] mt-1">
                  +{features.length - 7} {isEs ? 'más' : 'more'}...
                </div>
              )}
            </div>
          </div>

          {/* CTA button */}
          <button
            onClick={() => navigate('/subscription')}
            className="mt-5 w-full bg-gradient-to-r from-[#06539B] to-[#00BCD4] text-white text-[13px] font-[800] py-3.5 rounded-full hover:shadow-lg hover:shadow-[#06539B]/30 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Crown size={15} />
            {isEs ? `Upgrade a ${requiredPlan} — ${price}/mes` : `Upgrade to ${requiredPlan} — ${price}/mo`}
          </button>

          {/* Secondary link */}
          <button
            onClick={() => navigate('/subscription')}
            className="mt-2 w-full text-[11px] font-[700] text-[#9E9890] hover:text-[#06539B] transition-colors py-1"
          >
            {isEs ? 'Comparar todos los planes →' : 'Compare all plans →'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Small badge for sidebar nav items and feature lists
export function ProBadge({ plan = 'PRO' }: { plan?: 'PRO' | 'BUILDER' }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-[8px] font-[800] px-1.5 py-0.5 rounded-full bg-[#06539B]/10 text-[#06539B]">
      <Crown size={8} /> {plan}
    </span>
  )
}
